import type { Scope } from '../scopes';
import { scopeToSlug } from '../scopes';
import type { Task, TaskPriority, TaskStatus, TeamView } from '../types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';
const DEV_AUTO_LOGIN =
  import.meta.env.DEV && import.meta.env.VITE_DEV_AUTO_LOGIN === 'true';

let tokenProvider: () => string | null = () => null;

export function setAuthTokenProvider(provider: () => string | null): void {
  tokenProvider = provider;
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  if (DEV_AUTO_LOGIN) {
    const { mockApiFetch } = await import('./devMockApi');
    return mockApiFetch<T>(path, init);
  }
  const token = tokenProvider();
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...init?.headers,
      },
    });
  } catch (err) {
    throw new Error(
      err instanceof TypeError
        ? "Can't reach the task API. Check your connection or try again."
        : String(err)
    );
  }
  if (!response.ok) {
    const body = await response.text();
    if (response.status === 401) {
      throw new Error('Your session expired. Sign in again to continue.');
    }
    throw new Error(`API error ${response.status}: ${body}`);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export interface ListTasksResponse {
  tasks: Task[];
}

export async function listTasks(scope?: Scope | 'all' | 'deleted'): Promise<Task[]> {
  const qs = scope ? `?scope=${encodeURIComponent(scope)}` : '';
  const { tasks } = await apiFetch<ListTasksResponse>(`/tasks${qs}`);
  return tasks;
}

export interface CreateTaskInput {
  scope: Scope;
  title: string;
  description?: string;
  priority?: TaskPriority;
  dueDate?: string;
  assigneeId?: string;
  private?: boolean;
}

export async function createTask(input: CreateTaskInput): Promise<Task> {
  const { task } = await apiFetch<{ task: Task }>('/tasks', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return task;
}

export interface UpdateTaskInput {
  title?: string;
  description?: string;
  priority?: TaskPriority;
  pinned?: boolean;
  dueDate?: string | null;
  status?: TaskStatus;
  assigneeId?: string;
  private?: boolean;
}

export async function updateTask(
  scope: Scope,
  taskId: string,
  patch: UpdateTaskInput
): Promise<Task> {
  const slug = scopeToSlug(scope);
  const { task } = await apiFetch<{ task: Task }>(`/tasks/${slug}/${taskId}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  return task;
}

export async function deleteTask(scope: Scope, taskId: string): Promise<void> {
  const slug = scopeToSlug(scope);
  await apiFetch<void>(`/tasks/${slug}/${taskId}`, { method: 'DELETE' });
}

export async function purgeTask(scope: Scope, taskId: string): Promise<void> {
  const slug = scopeToSlug(scope);
  await apiFetch<void>(`/tasks/${slug}/${taskId}?purge=true`, { method: 'DELETE' });
}

export async function restoreTask(scope: Scope, taskId: string): Promise<Task> {
  const slug = scopeToSlug(scope);
  const { task } = await apiFetch<{ task: Task }>(`/tasks/${slug}/${taskId}/restore`, {
    method: 'POST',
  });
  return task;
}

export async function addNote(
  scope: Scope,
  taskId: string,
  body: string,
  transitionToPending = true
): Promise<Task> {
  const slug = scopeToSlug(scope);
  const res = await apiFetch<{ task: Task }>(`/tasks/${slug}/${taskId}/notes`, {
    method: 'POST',
    body: JSON.stringify({ body, transitionToPending }),
  });
  return res.task;
}

export async function moveTask(
  fromScope: Scope,
  taskId: string,
  toScope: Scope
): Promise<Task> {
  const slug = scopeToSlug(fromScope);
  const { task } = await apiFetch<{ task: Task }>(`/tasks/${slug}/${taskId}/move`, {
    method: 'POST',
    body: JSON.stringify({ toScope }),
  });
  return task;
}

export async function reclassifyTask(scope: Scope, taskId: string): Promise<Task> {
  const slug = scopeToSlug(scope);
  const { task } = await apiFetch<{ task: Task; reasoning?: string }>(
    `/tasks/${slug}/${taskId}/reclassify`,
    { method: 'POST' }
  );
  return task;
}

export async function getTeam(): Promise<TeamView> {
  return apiFetch<TeamView>('/team');
}

export interface UserSettings {
  userId: string;
  teamId: string;
  activityLastReadAt?: string;
  updatedAt: string;
}

export async function getSettings(): Promise<UserSettings> {
  const { settings } = await apiFetch<{ settings: UserSettings }>('/settings');
  return settings;
}

export async function updateSettings(
  patch: Partial<Pick<UserSettings, 'activityLastReadAt'>>
): Promise<UserSettings> {
  const { settings } = await apiFetch<{ settings: UserSettings }>('/settings', {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  return settings;
}

export interface ActivityItem {
  eventId: string;
  type: string;
  createdAt: string;
  actorUserId: string;
  taskId: string;
  scope: Scope;
  taskTitle: string;
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
}

export async function listActivity(limit = 30): Promise<ActivityItem[]> {
  const { activity } = await apiFetch<{ activity: ActivityItem[] }>(
    `/activity?limit=${limit}`
  );
  return activity;
}

export async function getAttachmentUrl(
  scope: Scope,
  taskId: string,
  filename: string
): Promise<string> {
  const slug = scopeToSlug(scope);
  const { url } = await apiFetch<{ url: string }>(
    `/tasks/${slug}/${taskId}/attachments/${encodeURIComponent(filename)}`
  );
  return url;
}
